import { createContext, useContext, type ReactNode } from 'react';

export interface ExternalNavigator {
    navigate(url: string): void;
}

/**
 * The only `window.location` write in the codebase — an explicit, justified
 * exception to routing.md rule 4. Handing the browser to the partner's
 * `redirect_uri` is not SPA navigation: it leaves this application for a
 * third-party origin, which is the entire point of the OAuth hand-off, and
 * `<Link>`/`useNavigate` cannot target another origin at all.
 *
 * Injectable so tests can assert on it directly — jsdom does not implement
 * navigation and would otherwise log "Not implemented" and swallow the call.
 */
export const browserNavigator: ExternalNavigator = {
    navigate: (url) => {
        window.location.assign(url);
    },
};

const ExternalNavigationContext = createContext<ExternalNavigator | null>(null);

export function useExternalNavigation(): ExternalNavigator {
    const value = useContext(ExternalNavigationContext);
    if (!value) {
        throw new Error(
            'useExternalNavigation must be used inside <ExternalNavigationProvider>'
        );
    }
    return value;
}

interface ExternalNavigationProviderProps {
    children: ReactNode;
    /** Injected by tests to supply a mocked navigator. Production passes nothing. */
    navigator?: ExternalNavigator;
}

export function ExternalNavigationProvider({
    children,
    navigator,
}: Readonly<ExternalNavigationProviderProps>) {
    const value = navigator ?? browserNavigator;

    return (
        <ExternalNavigationContext.Provider value={value}>
            {children}
        </ExternalNavigationContext.Provider>
    );
}
